import React from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
  children: React.ReactNode
}

function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ')
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-500 text-neutral-0 hover:bg-brand-600 active:bg-brand-700 shadow-xs',
  secondary:
    'bg-neutral-0 text-brand-600 border border-brand-500 hover:bg-brand-50 active:bg-brand-100',
  ghost:
    'bg-transparent text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200',
  danger:
    'bg-error-base text-neutral-0 hover:bg-error-dark shadow-xs',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1 text-sm rounded-md gap-2',
  md: 'px-4 py-2 text-base rounded-lg gap-2',
  lg: 'px-6 py-3 text-lg rounded-xl gap-3',
}

function Spinner(): React.ReactElement {
  return (
    <svg
      aria-hidden="true"
      className="animate-spin"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="2"
      />
      <path
        d="M14 8a6 6 0 0 0-6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  children,
  className,
  disabled,
  onClick,
  type = 'button',
  ...rest
}: ButtonProps): React.ReactElement {
  const isDisabled = disabled === true || loading

  function handleClick(event: React.MouseEvent<HTMLButtonElement>): void {
    if (isDisabled) return
    onClick?.(event)
  }

  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={type}
      aria-disabled={isDisabled}
      aria-busy={loading}
      onClick={handleClick}
      className={cx(
        'inline-flex items-center justify-center font-medium font-sans transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        className,
      )}
      {...rest}
    >
      {loading && (
        <span className="me-1">
          <Spinner />
        </span>
      )}
      {!loading && leftIcon !== undefined && (
        <span className="me-1" aria-hidden="true">
          {leftIcon}
        </span>
      )}
      {children}
      {rightIcon !== undefined && (
        <span className="ms-1" aria-hidden="true">
          {rightIcon}
        </span>
      )}
    </button>
  )
}
